import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/Card'
import { RefreshCw, Play, AlertTriangle } from 'lucide-react'
import { formatTimeShort, formatTime } from '@/lib/date'

type CommandOption = {
  type: 'select' | 'tenant_select' | 'flag' | 'text'
  label: string
  required?: boolean
  default?: boolean
  description?: string
  choices?: { value: string; label: string }[]
}

type CommandDef = {
  label: string
  description: string
  options: Record<string, CommandOption>
}

type Tenant = { code: string; name: string }

type Job = {
  id: number
  command: string
  environment: string
  options: string
  status: string
  task_arn: string | null
  task_id: string | null
  log_stream_name: string | null
  started_by: string
  exit_code: number | null
  stopped_reason: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
}

type LogEvent = { timestamp: number; message: string }

const CMD_TABS: { key: string; short: string }[] = [
  { key: 'opensearch:sync', short: 'os:sync' },
  { key: 'task:reindex', short: 'task:rx' },
  { key: 'activity:reindex', short: 'act:rx' },
  { key: 'report:reindex', short: 'rpt:rx' },
  { key: 'task:search-sync-retry', short: 'retry' },
]

export function OpenSearch() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [environment, setEnvironment] = useState<'dev' | 'prod'>('dev')
  const [commands, setCommands] = useState<Record<string, CommandDef>>({})
  const [selectedCmd, setSelectedCmd] = useState('opensearch:sync')
  const [optionValues, setOptionValues] = useState<Record<string, string | boolean>>({})
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [tenantsLoading, setTenantsLoading] = useState(false)

  const [jobs, setJobs] = useState<Job[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [activeJobId, setActiveJobId] = useState<number | null>(null)
  const [logEvents, setLogEvents] = useState<LogEvent[]>([])
  const [logToken, setLogToken] = useState<string | null>(null)
  const [executing, setExecuting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)

  // Redirect non-admin
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  // Load commands on mount
  useEffect(() => {
    fetchApi<Record<string, CommandDef>>('/opensearch/commands').then(setCommands).catch(() => {})
  }, [])

  // Load tenants when environment changes
  useEffect(() => {
    setTenantsLoading(true)
    fetchApi<Tenant[]>(`/opensearch/tenants/${environment}`)
      .then(setTenants)
      .catch(() => setTenants([]))
      .finally(() => setTenantsLoading(false))
  }, [environment])

  // Reset options when command changes
  useEffect(() => {
    const cmd = commands[selectedCmd]
    if (!cmd) return
    const defaults: Record<string, string | boolean> = {}
    for (const [key, opt] of Object.entries(cmd.options)) {
      defaults[key] = opt.type === 'flag' ? (opt.default ?? false) : ''
    }
    setOptionValues(defaults)
  }, [selectedCmd, commands])

  // Load job list
  const loadJobs = useCallback(async () => {
    setJobsLoading(true)
    try {
      const data = await fetchApi<Job[]>(`/opensearch/jobs?limit=20`)
      setJobs(data)
    } catch { /* ignore */ }
    finally { setJobsLoading(false) }
  }, [])

  useEffect(() => { loadJobs() }, [loadJobs])

  // Poll active job
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (activeJobId === null) return

    const poll = async () => {
      try {
        const job = await fetchApi<Job>(`/opensearch/jobs/${activeJobId}`)
        setJobs(prev => prev.map(j => j.id === job.id ? job : j))

        // Fetch logs
        const logsResp = await fetchApi<{ events: LogEvent[]; next_forward_token: string | null }>(
          `/opensearch/jobs/${activeJobId}/logs${logToken ? `?next_token=${encodeURIComponent(logToken)}` : ''}`
        )
        if (logsResp.events.length > 0) {
          setLogEvents(prev => {
            const existingTs = new Set(prev.map(e => `${e.timestamp}-${e.message}`))
            const newEvents = logsResp.events.filter(e => !existingTs.has(`${e.timestamp}-${e.message}`))
            return [...prev, ...newEvents]
          })
          setLogToken(logsResp.next_forward_token)
        }

        // Stop polling when done
        if (!['pending', 'running'].includes(job.status)) {
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
          loadJobs()
        }
      } catch { /* ignore */ }
    }

    poll() // immediate first poll
    pollRef.current = setInterval(poll, 5000)

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId])

  // Auto-scroll logs
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logEvents])

  function handleOptionChange(key: string, value: string | boolean) {
    setOptionValues(prev => ({ ...prev, [key]: value }))
  }

  function handleExecuteClick() {
    if (environment === 'prod') {
      setShowConfirm(true)
    } else {
      executeJob()
    }
  }

  async function executeJob() {
    setShowConfirm(false)
    setExecuting(true)
    try {
      const job = await fetchApi<Job>('/opensearch/jobs', {
        method: 'POST',
        body: JSON.stringify({
          command: selectedCmd,
          environment,
          options: optionValues,
        }),
      })
      setActiveJobId(job.id)
      setLogEvents([])
      setLogToken(null)
      await loadJobs()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to execute')
    } finally {
      setExecuting(false)
    }
  }

  function selectJob(job: Job) {
    setActiveJobId(job.id)
    setLogEvents([])
    setLogToken(null)
  }

  const cmdDef = commands[selectedCmd]

  function fmtTime(ts: string | null) {
    if (!ts) return '-'
    return formatTimeShort(ts)
  }

  function formatLogTime(ts: number) {
    return formatTime(ts)
  }

  function statusBadge(s: string, exitCode: number | null) {
    if (s === 'stopped' && exitCode === 0) {
      return <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">done</span>
    }
    if (s === 'stopped' && exitCode !== null && exitCode !== 0) {
      return <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">failed ({exitCode})</span>
    }
    const styles: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      running: 'bg-blue-500/20 text-blue-400',
      stopped: 'bg-gray-500/20 text-gray-400',
      unknown: 'bg-gray-500/20 text-gray-400',
    }
    return <span className={`text-xs px-2 py-0.5 rounded ${styles[s] || styles.unknown}`}>{s}</span>
  }

  function parseOptions(optStr: string): string {
    try {
      const opts = JSON.parse(optStr)
      return Object.entries(opts)
        .filter(([, v]) => v !== '' && v !== false)
        .map(([k, v]) => v === true ? `--${k}` : `--${k}=${v}`)
        .join(' ')
    } catch {
      return ''
    }
  }

  if (!user?.is_admin) return null

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">OpenSearch Reindex</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-[var(--color-border)] overflow-hidden text-sm">
            <button
              onClick={() => setEnvironment('dev')}
              className={`px-3 py-1 transition-colors ${
                environment === 'dev'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              DEV
            </button>
            <button
              onClick={() => setEnvironment('prod')}
              className={`px-3 py-1 transition-colors ${
                environment === 'prod'
                  ? 'bg-red-600 text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              PROD
            </button>
          </div>
          <button
            onClick={loadJobs}
            disabled={jobsLoading}
            className="p-1.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)] transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} className={jobsLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Command Tabs */}
      <div className="flex gap-1 flex-wrap">
        {CMD_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSelectedCmd(tab.key)}
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              selectedCmd === tab.key
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg-card)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            {tab.short}
          </button>
        ))}
      </div>

      {/* Command Card */}
      {cmdDef && (
        <Card>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[var(--color-text)]">{cmdDef.label}</span>
              <span className="text-xs text-[var(--color-text-muted)]">{cmdDef.description}</span>
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              {Object.entries(cmdDef.options).map(([key, opt]) => {
                if (opt.type === 'select') {
                  return (
                    <div key={key} className="min-w-[160px]">
                      <label className="block text-xs text-[var(--color-text-muted)] mb-1">{opt.label}</label>
                      <select
                        value={(optionValues[key] as string) || ''}
                        onChange={e => handleOptionChange(key, e.target.value)}
                        className="w-full px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                      >
                        <option value="">全て</option>
                        {opt.choices?.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  )
                }
                if (opt.type === 'tenant_select') {
                  return (
                    <div key={key} className="min-w-[160px]">
                      <label className="block text-xs text-[var(--color-text-muted)] mb-1">{opt.label}</label>
                      <select
                        value={(optionValues[key] as string) || ''}
                        onChange={e => handleOptionChange(key, e.target.value)}
                        disabled={tenantsLoading}
                        className="w-full px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                      >
                        <option value="">全テナント</option>
                        {tenants.map(t => (
                          <option key={t.code} value={t.code}>{t.code} - {t.name}</option>
                        ))}
                      </select>
                    </div>
                  )
                }
                if (opt.type === 'text') {
                  return (
                    <div key={key} className="min-w-[140px]">
                      <label className="block text-xs text-[var(--color-text-muted)] mb-1">{opt.label}</label>
                      <input
                        type="text"
                        value={(optionValues[key] as string) || ''}
                        onChange={e => handleOptionChange(key, e.target.value)}
                        placeholder={opt.label}
                        className="w-full px-2 py-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                      />
                    </div>
                  )
                }
                if (opt.type === 'flag') {
                  return (
                    <div key={key} className="pb-1">
                      <label className="flex items-center gap-1.5 text-sm text-[var(--color-text)]">
                        <input
                          type="checkbox"
                          checked={!!optionValues[key]}
                          onChange={e => handleOptionChange(key, e.target.checked)}
                          className="rounded"
                        />
                        {opt.label}
                      </label>
                      {opt.description && (
                        <p className="text-xs text-[var(--color-text-muted)] ml-5 mt-0.5">{opt.description}</p>
                      )}
                    </div>
                  )
                }
                return null
              })}

              <button
                onClick={handleExecuteClick}
                disabled={executing}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded text-white text-sm font-medium transition-opacity disabled:opacity-50 ${
                  environment === 'prod' ? 'bg-red-600 hover:bg-red-700' : 'bg-[var(--color-primary)] hover:opacity-90'
                }`}
              >
                <Play size={14} />
                {executing ? 'Executing...' : 'Execute'}
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Job History */}
      <Card title="Job History">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-muted)] text-left">
                <th className="py-2 px-3 font-medium">ID</th>
                <th className="py-2 px-3 font-medium">Command</th>
                <th className="py-2 px-3 font-medium">Options</th>
                <th className="py-2 px-3 font-medium">Env</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">User</th>
                <th className="py-2 px-3 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr
                  key={job.id}
                  onClick={() => selectJob(job)}
                  className={`border-b border-[var(--color-border)] last:border-0 cursor-pointer transition-colors ${
                    activeJobId === job.id
                      ? 'bg-[var(--color-primary)]/5'
                      : 'hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">#{job.id}</td>
                  <td className="py-2 px-3 text-[var(--color-text)]">{job.command}</td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)] text-xs font-mono max-w-[200px] truncate">
                    {parseOptions(job.options)}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      job.environment === 'prod' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {job.environment.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-2 px-3">{statusBadge(job.status, job.exit_code)}</td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">{job.started_by}</td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">{fmtTime(job.created_at)}</td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-[var(--color-text-muted)]">
                    No jobs yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Log Output */}
      {activeJobId !== null && (
        <Card title={`Log Output (Job #${activeJobId})`}>
          <div className="bg-[var(--color-bg)] rounded border border-[var(--color-border)] p-3 max-h-[400px] overflow-y-auto font-mono text-xs">
            {logEvents.length === 0 ? (
              <div className="text-[var(--color-text-muted)]">
                {jobs.find(j => j.id === activeJobId)?.status === 'pending'
                  ? 'Waiting for task to start...'
                  : 'Loading logs...'}
              </div>
            ) : (
              logEvents.map((e, i) => (
                <div key={i} className="text-[var(--color-text)] whitespace-pre-wrap break-all leading-5">
                  <span className="text-[var(--color-text-muted)]">[{formatLogTime(e.timestamp)}]</span>{' '}
                  {e.message}
                </div>
              ))
            )}
            <div ref={logEndRef} />
          </div>
        </Card>
      )}

      {/* Prod Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-bg-card)] border border-red-500/50 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-red-400" />
              <h3 className="text-base font-semibold text-red-400">Production Environment</h3>
            </div>
            <p className="text-sm text-[var(--color-text)] mb-2">
              本番環境に対して以下のコマンドを実行しますか？
            </p>
            <div className="bg-red-500/10 rounded p-2 mb-4 font-mono text-sm text-red-300">
              php artisan {selectedCmd}{' '}
              {Object.entries(optionValues)
                .filter(([, v]) => v !== '' && v !== false)
                .map(([k, v]) => v === true ? `--${k}` : `--${k}=${v}`)
                .join(' ')}
            </div>
            <div className="flex gap-2">
              <button
                onClick={executeJob}
                className="flex-1 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Execute on PROD
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
